import { ApiWrapperError } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';

/**
 * What a failed API call contributes to the Sentry event body.
 *
 * While ApiWrapperError was a plain object, Sentry's `eventFromPlainObject` attached
 * the whole thing as `extra.__serialized__`, which is where the status and the
 * backend's code and message were readable. Now that it is a real Error, Sentry builds
 * the event from name/message/stack alone and that payload is gone — so it has to be
 * put back deliberately, or the class change would cost more diagnostics than it won.
 */
const backendMessageOf = (backend: unknown): unknown => {
	if (typeof backend === 'object' && backend !== null && 'message' in backend) {
		return backend.message;
	}
	if (typeof backend === 'string') {
		return backend;
	}
	return undefined;
};

export const apiErrorContext = (error: unknown): Record<string, unknown> | undefined => {
	if (!(error instanceof ApiWrapperError)) {
		return undefined;
	}
	const backend = error.error;
	return {
		method: error.request?.method,
		endpoint: error.request?.endpoint,
		status: error.status,
		code:
			typeof backend === 'object' && backend !== null && 'code' in backend
				? backend.code
				: undefined,
		backendMessage: backendMessageOf(backend),
		retryAfterMs: error.retryAfterMs,
		sessionEndReason: error.sessionEndReason,
		alreadyReportedAsTransportFailure: error.alreadyReported,
	};
};

/**
 * Sentry groups JavaScript exceptions by their stack trace. Every `ApiWrapperError` is
 * constructed at a single line in `createErrorInstance`, inside the catch block of an
 * async function — V8 does not carry the awaiting caller into that resumption, so the
 * stack is the same two frames (`createErrorInstance`, `makeRequest`) for every endpoint
 * in the application. Left to the default grouping, every API failure app-wide collapses
 * into one issue.
 *
 * Returns a fingerprint keyed on the call that actually failed, or undefined to leave
 * Sentry's own grouping in place.
 */
export const apiErrorFingerprint = (error: unknown): string[] | undefined => {
	if (!(error instanceof ApiWrapperError) || !error.request) {
		return undefined;
	}
	return [
		'api-error',
		error.request.method,
		error.request.endpoint,
		String(error.status ?? 'no-response'),
	];
};

/**
 * The `beforeSend` body, exported so it can be exercised against a real Sentry client
 * rather than re-implemented in a test.
 *
 * Note what this can and cannot do: `beforeSend` runs after `_prepareEvent`, so the
 * fingerprint it sets is in time for server-side grouping but too late for
 * `dedupeIntegration`. Keeping two endpoints' failures apart at dedupe time is the job
 * of `describeApiFailure`, which puts the shaped call into the exception value.
 */
export const applyApiErrorMetadata = <T extends SentryEventShape>(
	event: T,
	failure: unknown
): T => {
	if (!event.fingerprint) {
		const fingerprint = apiErrorFingerprint(failure);
		if (fingerprint) {
			event.fingerprint = fingerprint;
		}
	}
	const context = apiErrorContext(failure);
	if (context) {
		event.contexts = { ...event.contexts, api: context };
	}
	return event;
};

interface SentryEventShape {
	fingerprint?: string[];
	contexts?: Record<string, unknown>;
}
